import { type Placement, type VirtualElement } from '@floating-ui/react-dom'
import React, { useMemo, useState } from 'react'

import { PopperAnchor } from './Anchor'
import { PopperArrow } from './Arrow'
import { PopperContent } from './Content'
import { PopperContext } from './context'
import { PopperPortal } from './Portal'

interface PopperProps {
  children: React.ReactNode
}
/**
 * A `<Popper>` is a generic, unstyled component for positioning floating
 * content relative to an anchor element. It can be used to create popovers,
 * tooltips, dropdown menus, comboboxes, etc.
 *
 * Wrap the anchor element with `<Popper.Anchor>` and the floating content with
 * `<Popper.Content>`. Use `<Popper.Portal>` to portal the floating content into
 * the `body` to avoid overflow issues.
 *
 * The optional `<Popper.Arrow>` can be used to render an arrow pointing to the
 * anchor element. The `<Popper.Arrow>` must be rendered as a descendant of the
 * `<Popper.Content>` element, as it is positioned relative to the content.
 */
export function Popper ({ children }: PopperProps) {
  const [anchor, setAnchor] = useState<HTMLElement | VirtualElement | null>(null)
  const [content, setContent] = useState<HTMLElement | null>(null)
  const [arrow, setArrow] = useState<HTMLElement | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)

  const contextValue = useMemo(
    () => ({
      anchor,
      arrow,
      content,
      placement,
      setAnchor,
      setArrow,
      setContent,
      setPlacement,
    }),
    [anchor, arrow, content, placement]
  )

  return (
    <PopperContext.Provider value={contextValue}>
      {children}
    </PopperContext.Provider>
  )
}

Popper.Anchor = PopperAnchor
Popper.Arrow = PopperArrow
Popper.Content = PopperContent
Popper.Portal = PopperPortal
